import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Права текущего пользователя.
 *
 * Источник — `permissions` / `flags` / `scope` из GET /auth/me. Бэкенд уже
 * учёл тариф и тумблеры модулей организации, поэтому здесь никаких доп.
 * проверок: пришёл ключ — значит можно.
 *
 * ⚠️ Этап 0: хук добавлен, существующие гейты (isAdmin / isWorker /
 *    user.role === '...') на него ещё НЕ переведены. Миграция — Этап 2,
 *    по модулю за раз. См. rbac_implementation_plan.md.
 *
 * Использование:
 *   const { can, scope } = usePerm();
 *   {can('income.view') && <IncomeBlock />}
 *   {can('quotes.see_cost') && <CostColumn />}
 */
export function usePerm() {
  const { user } = useAuth();

  const permissions = useMemo(
    () => new Set(user?.permissions || []),
    [user?.permissions]
  );
  const flags = useMemo(
    () => new Set(user?.flags || []),
    [user?.flags]
  );

  // ⚠️ can/canAny и весь возвращаемый объект ОБЯЗАНЫ быть мемоизированы.
  // Вызывающий код кладёт can в deps useEffect (этого требует линт
  // react-hooks/exhaustive-deps). Пересоздание ссылки на каждом рендере даёт
  // бесконечный цикл: эффект → setState → рендер → новый can → эффект.
  // Так на PROD 11.08.2026 улетело 1000+ запросов /settings/organization и
  // забило лимит соединений браузера — выглядело как отказ бэкенда.

  /** @param {string} key 'tasks.edit' | 'quotes.see_cost' */
  const can = useCallback(
    (key) => permissions.has(key) || flags.has(key),
    [permissions, flags]
  );

  /** Есть ли хоть одно право в модуле — для показа раздела в меню. */
  const canAny = useCallback((module) => {
    const prefix = module + '.';
    for (const k of permissions) if (k.startsWith(prefix)) return true;
    return false;
  }, [permissions]);

  return useMemo(() => ({
    can,
    canAny,
    scope: user?.scope || 'own',
    // Три области данных: 'all' | 'team' | 'own'.
    //   isOwnScope  — только свои записи (Работник)
    //   isOrgWide   — вся организация (Админ, Суперадмин)
    //   между ними 'team' — свои и подопечных (Бригадир): узкая выборка,
    //   но человек управляет людьми, поэтому управленческий UI ему нужен.
    isOwnScope: (user?.scope || 'own') === 'own',
    isOrgWide: (user?.scope || 'own') === 'all',
    isNarrowScope: (user?.scope || 'own') !== 'all',
    roleKey: user?.role_key || null,
    roleName: user?.role_name || null,
    // Пока матрица не доехала (старый токен, миграция не отработала) — набор
    // пуст. Вызывающий код может отличить «нет прав» от «данных ещё нет».
    ready: permissions.size > 0,
  }), [can, canAny, permissions, user?.scope, user?.role_key, user?.role_name]);
}
